import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, RefreshCw, Truck, PackageCheck, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Logo from '../components/Logo';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [returnTrips, setReturnTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const openWhatsApp = (phoneNumber) => {
    // Remove any special characters and spaces from phone number
    const cleanNumber = phoneNumber.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanNumber}`, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('reloadfreight_admin_auth');
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully.",
    });
    navigate('/admin/login');
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    setUpdatingStatus(bookingId);
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      
      // Update local state
      setBookings(bookings.map(b => 
        b.bookingId === bookingId ? { ...b, status: newStatus } : b
      ));
      
      toast({
        title: "Status Updated",
        description: `Booking status changed to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const updateTripStatus = async (tripId, newStatus) => {
    setUpdatingStatus(tripId);
    try {
      await axios.patch(`${API}/return-trips/${tripId}/status?status=${newStatus}`);
      
      // Update local state
      setReturnTrips(returnTrips.map(t => 
        t.tripId === tripId ? { ...t, status: newStatus } : t
      ));
      
      toast({
        title: "Status Updated",
        description: `Trip status changed to ${newStatus}`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, tripsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/return-trips`)
      ]);
      
      setBookings(bookingsRes.data);
      setReturnTrips(tripsRes.data);
      
      toast({
        title: "Data Refreshed",
        description: `Found ${bookingsRes.data.length} bookings and ${tripsRes.data.length} return trips`,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    // MongoDB stores time in UTC, so we need to convert to IST for display
    const date = new Date(dateString);
    // Add 5 hours 30 minutes to convert UTC to IST
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* WhatsApp Floating Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="h-6 w-6" />
      </button>

      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Logo size={48} />
              <span className="text-2xl font-bold text-gray-900">Reload Freight - Admin</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                onClick={fetchData} 
                variant="outline"
                disabled={loading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={() => navigate('/')} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
              <Button onClick={handleLogout} variant="destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Customer Bookings</p>
                  <p className="text-3xl font-bold text-blue-600">{bookings.length}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Truck className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Return Trip Posts</p>
                  <p className="text-3xl font-bold text-green-600">{returnTrips.length}</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <PackageCheck className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer Bookings */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Bookings</h2>
          {bookings.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                No customer bookings yet
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card key={booking.bookingId} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">
                          {booking.pickupLocation} → {booking.dropLocation}
                        </CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          Booking ID: {booking.bookingId}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={booking.status}
                          onChange={(e) => updateBookingStatus(booking.bookingId, e.target.value)}
                          disabled={updatingStatus === booking.bookingId}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 capitalize"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Load Weight</p>
                        <p className="text-sm text-gray-600">{booking.loadWeight}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Preferred Date</p>
                        <p className="text-sm text-gray-600">{booking.preferredDate}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Contact Number</p>
                        <div className="flex items-center gap-2">
                          <a href={`tel:${booking.contactNumber}`} className="text-blue-600 hover:underline text-sm">
                            {booking.contactNumber}
                          </a>
                          <Button
                            size="sm"
                            onClick={() => openWhatsApp(booking.contactNumber)}
                            className="bg-green-500 hover:bg-green-600 text-white h-7 px-2"
                          >
                            <MessageCircle className="h-3 w-3 mr-1" />
                            WhatsApp
                          </Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Submitted</p>
                        <p className="text-sm text-gray-600">{formatDate(booking.createdAt)}</p>
                      </div>
                      {booking.materialType && (
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Material Type</p>
                          <p className="text-sm text-gray-600">{booking.materialType}</p>
                        </div>
                      )}
                      {booking.loadingUnloadingIncluded && (
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Loading/Unloading</p>
                          <p className="text-sm text-gray-600 capitalize">{booking.loadingUnloadingIncluded}</p>
                        </div>
                      )}
                    </div>
                    {booking.notes && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-semibold text-gray-700">Notes</p>
                        <p className="text-sm text-gray-600 mt-1">{booking.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Return Trips */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Return Trip Posts</h2>
          {returnTrips.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                No return trip posts yet
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {returnTrips.map((trip) => (
                <Card key={trip.tripId} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">
                          {trip.fromLocation} → {trip.toLocation}
                        </CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          Trip ID: {trip.tripId}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={trip.status}
                          onChange={(e) => updateTripStatus(trip.tripId, e.target.value)}
                          disabled={updatingStatus === trip.tripId}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 capitalize"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Truck Type</p>
                        <p className="text-sm text-gray-600">{trip.truckType}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Available Date</p>
                        <p className="text-sm text-gray-600">{trip.availableDate}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Contact Number</p>
                        <div className="flex items-center gap-2">
                          <a href={`tel:${trip.contactNumber}`} className="text-green-600 hover:underline text-sm">
                            {trip.contactNumber}
                          </a>
                          <Button
                            size="sm"
                            onClick={() => openWhatsApp(trip.contactNumber)}
                            className="bg-green-500 hover:bg-green-600 text-white h-7 px-2"
                          >
                            <MessageCircle className="h-3 w-3 mr-1" />
                            WhatsApp
                          </Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Posted</p>
                        <p className="text-sm text-gray-600">{formatDate(trip.createdAt)}</p>
                      </div>
                      {trip.truckBodyType && (
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Body Type</p>
                          <p className="text-sm text-gray-600 capitalize">{trip.truckBodyType}</p>
                        </div>
                      )}
                      {trip.expectedPrice && (
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Expected Price</p>
                          <p className="text-sm text-gray-600">₹{trip.expectedPrice}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Logo size={40} />
              <span className="text-2xl font-bold">Reload Freight</span>
            </div>
            <p className="text-gray-300">
              © 2025 Reload Freight Technologies Pvt Ltd
            </p>
            <p className="text-gray-400">
              All rights reserved
            </p>
            <p className="text-gray-400">
              Contact: +91 9515698775
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminDashboard;
